import { useState, useEffect } from 'react'
import { Product, apiClient, getApiError } from '../api'
import { CheckCircle, Trash2, Check, Moon, Sun } from 'lucide-react'
import { useTheme } from '../contexts/ThemeContext'
import { Toast } from './Toast'

interface UploadScreenProps {
  onProductsLoaded: (products: Product[]) => void
  isLoading: boolean
}

export function UploadScreen({ onProductsLoaded, isLoading }: UploadScreenProps) {
  const { theme, toggleTheme, logo } = useTheme()
  const [dragActive, setDragActive] = useState(false)
  const [cachedFile, setCachedFile] = useState<string | null>(null)
  const [isCaching, setIsCaching] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!error) return
    const t = setTimeout(() => setError(null), 2000)
    return () => clearTimeout(t)
  }, [error])

  // Verificar se há arquivo em cache ao carregar
  useEffect(() => {
    checkCache()
  }, [])

  const checkCache = async () => {
    try {
      const response = await apiClient.getCache('upload.tsv')
      if (response.data.found && response.data.content) {
        setCachedFile('upload.tsv')
        console.log('Arquivo em cache encontrado')
      }
    } catch (error) {
      console.log('Nenhum arquivo em cache')
    }
  }

  const handleFileCache = async (content: string, filename: string) => {
    try {
      setIsCaching(true)
      await apiClient.uploadCache('upload.tsv', content)
      setCachedFile(filename)
      console.log('Arquivo cacheado com sucesso')
    } catch (error) {
      console.error('Erro ao cachear arquivo:', error)
    } finally {
      setIsCaching(false)
    }
  }

  const handleLoadFromCache = async () => {
    try {
      setIsCaching(true)
      const response = await apiClient.getCache('upload.tsv')

      if (!response.data.found || !response.data.content) {
        setError('Arquivo em cache não encontrado')
        return
      }

      processContent(response.data.content)
    } catch (error) {
      setError('Erro ao carregar arquivo do cache')
    } finally {
      setIsCaching(false)
    }
  }

  const handleClearCache = async () => {
    try {
      await apiClient.deleteCache('upload.tsv')
      setCachedFile(null)
      console.log('Cache deletado')
    } catch (error) {
      console.error('Erro ao deletar cache:', error)
    }
  }

  const processContent = (content: string) => {
    try {
      const lines = content.split('\n')
      const products: Product[] = []

      // Pular header e processar linhas
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim()
        if (!line) continue

        const parts = line.split('\t')
        if (parts.length >= 2) {
          products.push({
            codigo: parts[0].trim(),
            descricao: parts[1].trim(),
            marca: parts[2]?.trim() || undefined
          })
        }
      }

      if (products.length === 0) {
        setError('Nenhum produto encontrado no arquivo')
        return
      }

      console.log(`Carregados ${products.length} produtos`)
      onProductsLoaded(products)
    } catch (error) {
      setError('Erro ao processar arquivo: ' + (error instanceof Error ? error.message : 'desconhecido'))
    }
  }

  const handleFile = (file: File) => {
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string

        // Cachear o arquivo
        handleFileCache(content, file.name)

        // Processar conteúdo
        processContent(content)
      } catch (error) {
        setError('Erro ao processar arquivo: ' + (error instanceof Error ? error.message : 'desconhecido'))
      }
    }
    reader.readAsText(file)
  }

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(e.type === 'dragenter' || e.type === 'dragover')
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0])
    }
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0])
    }
  }

  return (
    <div className="min-h-screen bg-gradient-light dark:bg-gradient-dark transition-colors duration-300 flex items-center justify-center p-3 sm:p-4">
      <Toast message={error} type="error" />
      {/* Theme Toggle Button */}
      <button
        onClick={toggleTheme}
        className="absolute top-4 sm:top-6 right-4 sm:right-6 p-2 rounded-lg bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 transition-colors flex-shrink-0"
      >
        {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
      </button>

      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 sm:p-8 max-w-md w-full border border-gray-200 dark:border-slate-700">
        {/* Logo */}
        <div className="flex justify-center mb-4 sm:mb-6">
          <img
            src={logo}
            alt="Seridóplast"
            className="h-12 sm:h-16 w-auto object-contain"
          />
        </div>

        {/* Header */}
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#E5191F] to-[#F03B40] dark:from-[#D9B20F] dark:to-[#FCD016] bg-clip-text text-transparent mb-2">
            SP Image Manager
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300">Conferência automática de imagens do catálogo</p>
        </div>

        {/* Cache disponível */}
        {cachedFile && (
          <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-green-50 dark:bg-green-900/20 border-2 border-green-200 dark:border-green-800 rounded-lg">
            <p className="text-xs sm:text-sm font-semibold text-green-800 dark:text-green-400 mb-2 sm:mb-3 flex items-center gap-2">
              <CheckCircle size={16} className="flex-shrink-0" />
              Arquivo em Cache
            </p>
            <p className="text-xs text-green-700 dark:text-green-300 mb-2 sm:mb-3">Você tem um arquivo armazenado temporariamente</p>
            <div className="flex gap-2">
              <button
                onClick={handleLoadFromCache}
                disabled={isCaching || isLoading}
                className="flex-1 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white font-semibold py-2 rounded-lg transition disabled:opacity-50 text-xs sm:text-sm flex items-center justify-center gap-1"
              >
                <Check size={12} className="sm:block hidden" />
                <Check size={10} className="sm:hidden" />
                {isCaching ? <span className="hidden sm:inline">Carregando...</span> : <span className="hidden sm:inline">Carregar</span>}
                {isCaching ? <span className="sm:hidden">C...</span> : <span className="sm:hidden">Ok</span>}
              </button>
              <button
                onClick={handleClearCache}
                disabled={isCaching || isLoading}
                className="flex-1 bg-gradient-to-r from-red-600 to-red-500 hover:from-red-700 hover:to-red-600 text-white font-semibold py-2 rounded-lg transition disabled:opacity-50 text-xs sm:text-sm flex items-center justify-center gap-1"
              >
                <Trash2 size={12} className="sm:block hidden" />
                <Trash2 size={10} className="sm:hidden" />
                {isCaching ? <span className="hidden sm:inline">Limpando...</span> : <span className="hidden sm:inline">Limpar</span>}
                {isCaching ? <span className="sm:hidden">L...</span> : <span className="sm:hidden">Del</span>}
              </button>
            </div>
          </div>
        )}

        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-6 sm:p-8 text-center transition ${
            dragActive
              ? 'border-[#E5191F] dark:border-[#FCD016] bg-[#E5191F]/5 dark:bg-[#FCD016]/5'
              : 'border-gray-300 dark:border-slate-600 hover:border-[#E5191F] dark:hover:border-[#FCD016]'
          }`}
        >
          <div className="mb-3 sm:mb-4">
            <svg
              className="mx-auto h-10 sm:h-12 w-10 sm:w-12 text-[#E5191F] dark:text-[#FCD016]"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
            >
              <path
                d="M28 8H12a4 4 0 00-4 4v20a4 4 0 004 4h24a4 4 0 004-4V20m-14-8v8m0 0l3-3m-3 3l-3-3"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <input
            type="file"
            accept=".txt,.csv,.tsv"
            onChange={handleFileInput}
            disabled={isLoading || isCaching}
            className="hidden"
            id="file-input"
          />

          <label
            htmlFor="file-input"
            className="cursor-pointer block"
          >
            <span className="bg-gradient-to-r from-[#E5191F] to-[#F03B40] dark:from-[#D9B20F] dark:to-[#FCD016] bg-clip-text text-transparent font-semibold hover:opacity-80 text-sm sm:text-base">
              Clique para selecionar
            </span>
            <span className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm"> ou arraste arquivo aqui</span>
          </label>

          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">TSV ou CSV com colunas: código, descrição, marca</p>
        </div>

        {(isLoading || isCaching) && (
          <div className="mt-4 sm:mt-6 flex justify-center">
            <div className="animate-spin">
              <svg className="h-8 w-8 text-[#E5191F] dark:text-[#FCD016]" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
